import { z } from 'zod';
import {
  cents,
  entityBase,
  id,
  organizationId,
  timestamp,
  paginationQuery,
  paginatedResult,
} from './common.js';
import { shiftEarning } from './billing.js';

export const workStatus = z.enum(['ready', 'submitted', 'disputed', 'approved']);
export type WorkStatus = z.infer<typeof workStatus>;

export const workRecord = entityBase.extend({
  bookingId: id,
  professionalId: id,
  status: workStatus.exclude(['ready']),
  startsAt: timestamp,
  endsAt: timestamp,
  breakMinutes: z.number().int().nonnegative(),
  note: z.string().nullable(),
  revision: z.number().int().positive(),
  submittedByUserId: id,
  submittedAt: timestamp,
  reviewedByUserId: id.nullable(),
  reviewedAt: timestamp.nullable(),
  disputeReason: z.string().nullable(),
});
export type WorkRecord = z.infer<typeof workRecord>;

export const workRecordEvent = z.object({
  id,
  organizationId,
  workRecordId: id,
  revision: z.number().int().positive(),
  kind: z.enum(['submitted', 'resubmitted', 'disputed', 'approved']),
  actorUserId: id,
  startsAt: timestamp,
  endsAt: timestamp,
  breakMinutes: z.number().int().nonnegative(),
  note: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: timestamp,
});
export type WorkRecordEvent = z.infer<typeof workRecordEvent>;

/** A completed booking and its actual-hours record, if submitted. Actions are resolved by the API. */
export const workView = z.object({
  id: id.nullable(),
  bookingId: id,
  shiftId: id,
  professionalId: id,
  locationId: id,
  locationName: z.string(),
  locationTimezone: z.string(),
  professionalName: z.string(),
  shiftStartsAt: timestamp,
  shiftEndsAt: timestamp,
  agreedRateCents: cents.nullable(),
  quotedGrossCents: cents.nullable(),
  quotedOfficeFeeCents: cents.nullable(),
  quotedOfficeTotalCents: cents.nullable(),
  status: workStatus,
  startsAt: timestamp.nullable(),
  endsAt: timestamp.nullable(),
  breakMinutes: z.number().int().nonnegative().nullable(),
  note: z.string().nullable(),
  disputeReason: z.string().nullable(),
  submittedAt: timestamp.nullable(),
  reviewedAt: timestamp.nullable(),
  revision: z.number().int().nonnegative(),
  earning: shiftEarning.nullable(),
  canSubmit: z.boolean(),
  canApprove: z.boolean(),
  canDispute: z.boolean(),
});
export type WorkView = z.infer<typeof workView>;

export const workSubmitInput = z
  .object({
    startsAt: z.string().datetime({ offset: true }),
    endsAt: z.string().datetime({ offset: true }),
    breakMinutes: z.number().int().min(0).max(1439),
    note: z.string().trim().max(2000).optional(),
  })
  .refine((value) => Date.parse(value.endsAt) > Date.parse(value.startsAt), {
    path: ['endsAt'],
    message: 'End time must be after start time',
  });
export type WorkSubmitInput = z.infer<typeof workSubmitInput>;

export const workReviewInput = z.object({ revision: z.number().int().positive() });
export type WorkReviewInput = z.infer<typeof workReviewInput>;

export const workDisputeInput = workReviewInput.extend({
  reason: z.string().trim().min(1).max(2000),
});
export type WorkDisputeInput = z.infer<typeof workDisputeInput>;

export const financialSettings = z.object({
  officeFeeBasisPoints: z.number().int().min(0).max(10000),
});
export type FinancialSettings = z.infer<typeof financialSettings>;

export const organizationFinancialSettings = entityBase.extend({
  officeFeeBasisPoints: z.number().int().min(0).max(10000),
  updatedByUserId: id.nullable(),
});
export type OrganizationFinancialSettings = z.infer<typeof organizationFinancialSettings>;

export const workHistoryQuery = paginationQuery.extend({
  workspace: z.enum(['professional', 'office']),
});
export const workHistoryPage = z.object({
  bookingId: id,
  timezone: z.string(),
  events: paginatedResult(workRecordEvent),
});
export type WorkHistoryPage = z.infer<typeof workHistoryPage>;
